// Unpublished Work © 2026 Deere & Company.
/* eslint-disable max-statements */
import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {Box, Card, Stack, Typography} from '@deere/isg.component-library';
import {fetchFleetAndEquipment, setWorkItems, setJobsToRemove, setOperatorsToDelete, setRowOrder} from 'Src/ui-core/store/actions/setup-workboard';
import {v4} from 'uuid';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';
import {arrayMove} from '@dnd-kit/sortable';
import {JOB} from 'OnLabor/workboard/constants/workboard-creation-flows';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';
import AssignDetailWrapper from './assign-detail-wrapper';
import {cloneDeep} from 'lodash';
import dialogTypes from 'Ui/components/common/dialog-types';
import {openDialog} from 'Src/ui-core/store/actions/dialogs';
import {TRUE} from 'Ui/constants/membership-properties';
import {GOLF_FLEET_TO_EQUIPMENT} from 'Common/constants/isg-toggles';
import {
    getGroupKey, buildWorkItemKey, updateWorkItem, mapIdsForWorkItem,
    appendNewItemsToEndByGroup, filterWorkItems, renderExistingWorkItems,
    sortWorkItemsBySeq, getAssignedEquipment, createPersist
} from './utils/work-item-utils';
import {
    getRows, renderRowsByOrder, getTotalOperatorCount, getFormattedDateAndTime,
    getMultiSelectItems, addNewItemToMultiselectValue, removeValuesFromMultiSelect, pruneMultiSelectValue
} from './utils/assign-helpers';
import {getColumns} from './assign-columns';

export {createPersist};

function workboardDetailsEl(label, value) {
    return (
        <Stack
            alignItems={'baseline'}
            direction='row'
            display={'flex'}
            spacing={2}
        >
            <Typography variant='labelLargeSemibold'>{`${label}: `}</Typography>
            <Typography
                noWrap
                variant='bodyMedium'
            >{value || '---'}</Typography>
        </Stack>
    );
}

function AssignWorkboard(props) {
    const {
        details,
        getFleetEquipment,
        openAddAssignmentsDialog,
        isMobile,
        isSaving,
        isgToggles,
        saveWorkItems,
        selectedOperators,
        selectedJobs,
        translations,
        workItems,
        selectJobsToRemove,
        selectOperatorsToDelete,
        rowOrder,
        selectRowOrder,
        membership,
        groupBy
    } = props;

    const persist = React.useMemo(
        () => createPersist(isMobile, saveWorkItems),
        [isMobile, saveWorkItems]
    );

    // Clean timers after unmounting
    React.useEffect(() => () => persist.cancel(), [persist]);

    const {
        formattedDate, formattedTime
    } = getFormattedDateAndTime(details);

    const selectedJobsIds = React.useMemo(() => selectedJobs.map((job) => job.id), [selectedJobs]);

    const selectedOperatorsIds = React.useMemo(() => selectedOperators.map((op) => op.id), [selectedOperators]);

    const assignedEquipmentIds = React.useMemo(() => getAssignedEquipment(workItems), [workItems]);

    const [isSideComponentOpen, setIsSideComponentOpen] = React.useState(false);

    const [workItemsCopy, setWorkItemsCopy] = React.useState(cloneDeep(workItems));

    React.useEffect(() => {
        setWorkItemsCopy(cloneDeep(workItems));
    }, [workItems]);

    const [loading, setLoading] = React.useState(true);

    const [rows, setRows] = React.useState([]);

    React.useEffect(() => {
        renderRowsByOrder(getRows(selectedJobs, selectedOperators, groupBy), rows, setRows, rowOrder, selectRowOrder, setLoading);
    }, [selectedJobs, selectedOperators, groupBy]);

    const totalOperatorCount = getTotalOperatorCount(rows);

    const [multiSelectValue, setMultiSelectValue] = React.useState({});

    const [selectedRow, setSelectedRow] = React.useState(null);
    const previousRowIds = React.useRef([]);

    const multiSelectItems = getMultiSelectItems(multiSelectValue, selectedOperators, selectedJobs, groupBy, translations);

    const openRowDetails = (row) => {
        setSelectedRow(row);
        setIsSideComponentOpen(true);
    };

    const handleRowOrderChange = React.useCallback((params) => {
        const clone = [...rowOrder];
        const [moved] = clone.splice(params.oldIndex, 1);

        clone.splice(params.targetIndex, 0, moved);

        setRows((prevRows) => {
            const mapPrev = new Map(prevRows.map((r) => [r.id, r]));

            return clone.map((id, idx) => ({
                ...mapPrev.get(id),
                priority: idx + 1
            }));
        });
        selectRowOrder(clone);
    }, [rowOrder]);

    React.useEffect(() => {
        getFleetEquipment(Boolean(isgToggles?.[GOLF_FLEET_TO_EQUIPMENT]));
    }, []);

    const selectedWorkItems = React.useMemo(() => {
        if (!selectedRow) {
            return [];
        }

        const entries = Object.entries(workItemsCopy)
            .map(([compositeKey, it]) => ({
                ...it,
                compositeKey
            }))
            .filter((it) => getGroupKey(groupBy, it) === selectedRow.id);

        return sortWorkItemsBySeq(entries);
    }, [workItemsCopy, selectedRow, groupBy]);

    const selectedWorkItemsIds = React.useMemo(
        () => selectedWorkItems.map((w) => w.workItemId),
        [selectedWorkItems]
    );

    const DEFAULT_WORKITEM_VALUES = React.useRef({
        note: '',
        route: null,
        equipment: []
    });

    React.useEffect(() => {
        if (isSaving && isSideComponentOpen) {
            setIsSideComponentOpen(false);
        }
    }, [isSaving]);

    React.useEffect(() => {
        const rowIds = groupBy === JOB ? selectedJobsIds : selectedOperatorsIds;
        const valueIds = groupBy === JOB ? selectedOperatorsIds : selectedJobsIds;
        const rowIdsChanged = previousRowIds.current.length !== rowIds.length ||
            previousRowIds.current.some((id) => !rowIds.includes(id));

        setMultiSelectValue((prev) => pruneMultiSelectValue(prev, rowIds, valueIds));

        if (rowIdsChanged && selectedRow && !rowIds.includes(selectedRow.id)) {
            setSelectedRow(null);
            setIsSideComponentOpen(false);
        }
        previousRowIds.current = rowIds;
    }, [selectedJobsIds, selectedOperatorsIds, groupBy, selectedRow]);

    React.useEffect(() => {
        const rowIds = groupBy === JOB ? selectedJobsIds : selectedOperatorsIds;

        if (Object.keys(workItemsCopy).length > 0 && Object.keys(multiSelectValue).length === 0) {
            renderExistingWorkItems(
                workItemsCopy,
                setMultiSelectValue,
                groupBy === JOB ? selectedOperatorsIds : selectedJobsIds,
                rowIds,
                saveWorkItems,
                groupBy
            );
            return;
        }

        const next = {};
        const allIds = [];

        for (const [rowId, ids] of Object.entries(multiSelectValue)) {
            allIds.push(...ids);
            for (const id of ids) {
                const key = groupBy === JOB ? buildWorkItemKey(rowId, id) : buildWorkItemKey(id, rowId);
                const {
                    jobId, operatorId
                } = mapIdsForWorkItem(groupBy, rowId, id);
                const jobTemplate = selectedJobs.find((job) => job.jobTemplateId === key.split('::')[0]);
                const estDuration = jobTemplate?.estDuration ? jobTemplate.estDuration : 0;

                next[key] =
                    workItemsCopy[key] ??
                    {
                        workItemId: v4(),
                        jobId,
                        operatorId,
                        estDuration,
                        teamMember: selectedOperators.find((op) => op.id === operatorId),
                        actualDuration: membership.properties.workboard_copy_estimated_to_actual === TRUE ? estDuration : 0,
                        ...DEFAULT_WORKITEM_VALUES.current
                    };
            }
        }

        const prevKeys = Object.keys(workItemsCopy);
        const nextKeys = Object.keys(next);
        const newWorkItems = prevKeys.length > nextKeys.length ? workItemsCopy : next;

        const sequenced = appendNewItemsToEndByGroup(newWorkItems, groupBy);

        saveWorkItems(filterWorkItems(sequenced, allIds, groupBy, rowIds, multiSelectValue));
    }, [multiSelectValue, selectedOperators, selectedJobs]);

    const handleDragEnd = React.useCallback(({
        active, over
    }) => {
        if (!over || active.id === over.id) {
            return null;
        }

        const next = {
            ...workItemsCopy
        };

        const groupId = selectedRow.id;

        const entries = Object.entries(next)
            .filter(([, it]) => getGroupKey(groupBy, it) === groupId);

        const byId = new Map(entries.map(([k, it]) => [it.workItemId, [k, it]]));

        const order = entries
            .sort(([, a], [, b]) => a.seq - b.seq)
            .map(([, it]) => it.workItemId);

        const oldIndex = order.indexOf(active.id);
        const newIndex = order.indexOf(over.id);

        const moved = arrayMove(order, oldIndex, newIndex);

        moved.forEach((id, index) => {
            const [k, it] = byId.get(id);

            next[k] = {
                ...it,
                seq: index + 1
            };
        });
        return next;
    }, [workItemsCopy, selectedRow, groupBy, saveWorkItems]);

    return (
        <Box>
            <Box
                gap={'1rem'}
                pt={'1rem'}
                px={'1rem'}
            >
                <Card
                    sx={{
                        p: '1rem'
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gap: '16px',
                            gridTemplateColumns: {
                                xs: 'repeat(2, 1fr)',
                                md: 'auto auto auto auto 1fr'
                            }
                        }}
                    >
                        {workboardDetailsEl(translations.NAME, details.name)}
                        {workboardDetailsEl(translations.DATE, formattedDate)}
                        {workboardDetailsEl(translations.START_TIME || 'Start Time', formattedTime)}
                        {workboardDetailsEl(translations.PRO_GOLF_PIN_LOCATION, details.pinLocation)}
                        <Stack
                            alignItems={'baseline'}
                            direction='row'
                            display={'flex'}
                            spacing={2}
                            sx={{
                                minWidth: 0,
                                flex: '1 1 0'
                            }}
                        >
                            <Typography variant='labelLargeSemibold'>{`${translations.NOTE}: `}</Typography>
                            <Typography
                                noWrap
                                sx={{
                                    minWidth: 0
                                }}
                                variant='bodyMedium'
                            >{details.note || details.notes || '---'}</Typography>
                        </Stack>
                    </Box>
                </Card>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    height: '100%'
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        maxHeight: '100%',
                        p: '1rem',
                        mb: '5em'
                    }}
                >
                    <DataGridWrapper
                        aggregation={{
                            priority: {
                                type: 'size',
                                config: {
                                    suffix: translations.TOTALS
                                }
                            },
                            jobName: {
                                type: 'size',
                                config: {
                                    suffix: translations.JOBS
                                }
                            },
                            operatorName: {
                                type: 'size',
                                config: {
                                    suffix: translations.OPERATORS
                                }
                            },
                            equipment: {
                                type: 'size',
                                config: {
                                    suffix: translations.ASSIGNED_EQUIPMENT
                                }
                            }
                        }}
                        columns={getColumns(isMobile, translations, workItemsCopy, multiSelectItems, multiSelectValue, setMultiSelectValue, totalOperatorCount, openRowDetails, groupBy, openAddAssignmentsDialog)}
                        enableDrillIn={true}
                        loading={loading}
                        maxHeightOffset={400}
                        onDrillIn={(params) => {
                            openRowDetails(params.row);
                        }}
                        onRowClick={() => { }}
                        onRowOrderChange={handleRowOrderChange}
                        onRowSelectionModelChange={(selectionModel) => {
                            groupBy === JOB ? selectJobsToRemove({
                                jobsToRemove: [...selectionModel.ids]
                            }) : selectOperatorsToDelete({
                                operatorsToDelete: [...selectionModel.ids]
                            });
                        }}
                        rowReordering={!isMobile}
                        rowSelectionMode={isMobile ? '' : 'multiple'}
                        rows={rows}
                        searchable={false}
                        showDeleteColumn={false}
                        slotProps={{
                            root: {
                                disableColumnMenu: true,
                                disableRowSelectionExcludeModel: true
                            }
                        }}
                        translations={translations}
                    />
                </Box>
                <AssignDetailWrapper
                    buildWorkItemKey={buildWorkItemKey}
                    closeHandler={(itemsAdded) => {
                        setIsSideComponentOpen(false);

                        if (isMobile) {
                            removeValuesFromMultiSelect(setMultiSelectValue, selectedRow.id, itemsAdded);
                            setWorkItemsCopy(workItems);
                        } else {
                            persist.flush();
                            saveWorkItems(workItemsCopy);
                        }
                    }}
                    equipmentInUse={assignedEquipmentIds}
                    groupBy={groupBy}
                    handleAddedItems={(ids) => addNewItemToMultiselectValue(setMultiSelectValue, selectedRow.id, ids)}
                    handleDragEnd={(e) => {
                        const updated = handleDragEnd(e);

                        if (updated) {
                            setWorkItemsCopy(updated);
                            persist.run(updated);
                        }
                    }}
                    isMobile={isMobile}
                    isOpen={isSideComponentOpen}
                    membership={membership}
                    onDialogConfirm={() => {
                        saveWorkItems(workItemsCopy);
                        setIsSideComponentOpen(false);
                    }}
                    openAddAssignmentsDialog={openAddAssignmentsDialog}
                    selectedWorkItems={selectedWorkItems}
                    selectedWorkItemsIds={selectedWorkItemsIds}
                    title={groupBy === JOB ? selectedRow?.jobName : selectedRow?.operatorName}
                    updateWorkItem={({
                        key, values
                    }) => {
                        const updated = updateWorkItem(key, values, DEFAULT_WORKITEM_VALUES, workItemsCopy);

                        setWorkItemsCopy(updated);
                        persist.run(updated);
                    }}
                />
            </Box>
        </Box>
    );
}

export function mapStateToProps(state) {
    return {
        membership: state.membership,
        details: state.setupWorkboard.details,
        isSaving: state.setupWorkboard.isSaving,
        isgToggles: state.account.isgToggles,
        selectedOperators: state.setupWorkboard.operators.selected,
        selectedJobs: state.setupWorkboard.jobs.selectedJobs,
        selectedMowingPatterns: state.setupWorkboard.cutsAndDirections.selectedMowingDirections,
        translations: state.translations,
        myJdPermissions: state.account.myJdPermissions,
        orgId: state.membership.orgId,
        workItems: state.setupWorkboard.workItems,
        rowOrder: state.setupWorkboard.rowOrder
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        getFleetEquipment(props) {
            return dispatch(fetchFleetAndEquipment(props));
        },
        saveWorkItems(props) {
            return dispatch(setWorkItems(props));
        },
        selectJobsToRemove(props) {
            return dispatch(setJobsToRemove(props));
        },
        selectOperatorsToDelete(props) {
            return dispatch(setOperatorsToDelete(props));
        },
        selectRowOrder(props) {
            return dispatch(setRowOrder(props));
        },
        openAddAssignmentsDialog(props) {
            dispatch(openDialog(dialogTypes.ADD_ASSIGNMENTS_DIALOG, props));
        }
    };
}

AssignWorkboard.propTypes = {
    details: PropTypes.object,
    getFleetEquipment: PropTypes.func,
    groupBy: PropTypes.string,
    isgToggles: PropTypes.object,
    isMobile: PropTypes.bool,
    isSaving: PropTypes.bool,
    membership: PropTypes.membership,
    openAddAssignmentsDialog: PropTypes.func,
    rowOrder: PropTypes.array,
    saveWorkItems: PropTypes.func,
    selectedJobs: PropTypes.arrayOf(PropTypes.object),
    selectedOperators: PropTypes.arrayOf(PropTypes.object),
    selectJobsToRemove: PropTypes.func,
    selectOperatorsToDelete: PropTypes.func,
    selectRowOrder: PropTypes.func,
    translations: PropTypes.translations,
    workItems: PropTypes.object
};

export default connect(mapStateToProps, mapDispatchToProps)(MediaQuery(MOBILE_MEDIA_QUERY)(AssignWorkboard));
